import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle, Loader2, User, Calendar, Wrench, ChevronRight, ChevronLeft } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputStyles = "bg-black/50 border-red-900/30 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E60000] focus-visible:border-[#E60000] h-11 rounded-none font-body";
const labelStyles = "text-white/70 text-xs uppercase tracking-widest font-body";

const STEPS_FR = [
  { num: 1, label: "Contact", icon: User },
  { num: 2, label: "Événement", icon: Calendar },
  { num: 3, label: "Besoins", icon: Wrench },
];
const STEPS_EN = [
  { num: 1, label: "Contact", icon: User },
  { num: 2, label: "Event", icon: Calendar },
  { num: 3, label: "Needs", icon: Wrench },
];

function ProgressBar({ step, steps }) {
  return (
    <div className="mb-10" data-testid="form-progress">
      {/* Step indicators */}
      <div className="flex items-center justify-between mb-3">
        {steps.map((s, i) => {
          const isActive = step >= s.num;
          const isCurrent = step === s.num;
          return (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                    isCurrent
                      ? "border-[#E60000] bg-[#E60000] shadow-[0_0_20px_rgba(230,0,0,0.4)]"
                      : isActive
                      ? "border-[#E60000] bg-[#E60000]/20"
                      : "border-white/15 bg-black/50"
                  }`}
                  data-testid={`step-indicator-${s.num}`}
                >
                  {isActive && step > s.num ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  ) : (
                    <s.icon className={`w-4 h-4 ${isCurrent ? "text-white" : isActive ? "text-[#E60000]" : "text-white/30"}`} />
                  )}
                </div>
                <span className={`mt-2 text-[10px] uppercase tracking-widest font-bold transition-colors duration-300 ${isCurrent ? "text-[#E60000]" : isActive ? "text-white/60" : "text-white/20"}`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="flex-1 h-[2px] mx-3 mt-[-18px] relative overflow-hidden bg-white/10">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-[#E60000]"
                    initial={{ width: "0%" }}
                    animate={{ width: step > s.num ? "100%" : "0%" }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
};

export default function Contact() {
  const { t, lang } = useLang();
  const steps = lang === "fr" ? STEPS_FR : STEPS_EN;
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    event_type: "", event_date: "", start_time: "", end_time: "",
    location: "", num_people: "", duration: "",
    options: [], budget: "", message: "",
  });
  const [status, setStatus] = useState("idle");

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const toggleOption = (opt) => setForm((p) => ({
    ...p, options: p.options.includes(opt) ? p.options.filter((o) => o !== opt) : [...p.options, opt],
  }));

  const goNext = () => { setDir(1); setStep((s) => Math.min(s + 1, 3)); };
  const goBack = () => { setDir(-1); setStep((s) => Math.max(s - 1, 1)); };

  const canGoNext = () => {
    if (step === 1) return form.first_name && form.last_name && form.email && form.phone;
    if (step === 2) return form.event_type && form.event_date && form.location;
    return true;
  };

  const handleSubmit = async () => {
    setStatus("loading");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <section id="soumission" className="relative py-24 md:py-32 bg-[#050505]" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle className="w-16 h-16 text-[#E60000] mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white mb-4">{t.contact.success_title}</h2>
            <p className="font-body text-[#A3A3A3] text-lg">{t.contact.success_text}</p>
            <button
              onClick={() => { setStatus("idle"); setStep(1); setForm({ first_name: "", last_name: "", email: "", phone: "", event_type: "", event_date: "", start_time: "", end_time: "", location: "", num_people: "", duration: "", options: [], budget: "", message: "" }); }}
              className="mt-8 border border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white px-6 py-3 text-sm uppercase tracking-widest font-bold transition-all duration-300"
              data-testid="reset-form-btn"
            >
              {t.contact.new_quote}
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="soumission" className="relative py-16 md:py-32 bg-[#050505]" data-testid="contact-section">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#E60000]/5 rounded-full blur-[150px]" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#E60000]" />
            <span className="text-[#E60000] text-sm uppercase tracking-[0.3em] font-medium font-body">{t.contact.section_tag}</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold uppercase text-white" data-testid="contact-title">
            {t.contact.title_line1}<br /><span className="text-[#E60000]">{t.contact.title_line2}</span>
          </h2>
          <p className="font-body text-[#A3A3A3] mt-4 max-w-xl">{t.contact.subtitle}</p>
        </div>

        {/* Progress Bar */}
        <ProgressBar step={step} steps={steps} />

        {/* Form */}
        <div data-testid="quote-form">
          <div className="min-h-[340px] relative overflow-hidden">
            <AnimatePresence mode="wait" custom={dir}>
              {/* Step 1: Contact */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="space-y-5"
                  data-testid="form-step-1"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.first_name} *</Label>
                      <Input required value={form.first_name} onChange={(e) => handleChange("first_name", e.target.value)} className={inputStyles} data-testid="input-first-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.last_name} *</Label>
                      <Input required value={form.last_name} onChange={(e) => handleChange("last_name", e.target.value)} className={inputStyles} data-testid="input-last-name" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.email} *</Label>
                    <Input required type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className={inputStyles} data-testid="input-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.phone} *</Label>
                    <Input required type="tel" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder="514 000-0000" className={inputStyles} data-testid="input-phone" />
                  </div>
                </motion.div>
              )}

              {/* Step 2: Event Details */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="space-y-5"
                  data-testid="form-step-2"
                >
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.event_type} *</Label>
                    <Select value={form.event_type} onValueChange={(v) => handleChange("event_type", v)}>
                      <SelectTrigger className={`${inputStyles} [&>span]:text-left`} data-testid="select-event-type"><SelectValue placeholder={t.contact.event_type_placeholder} /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-red-900/30 text-white">{t.contact.event_types.map((type) => (<SelectItem key={type} value={type} className="focus:bg-[#E60000]/20 focus:text-white font-body">{type}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.event_date} *</Label>
                      <Input required type="date" value={form.event_date} onChange={(e) => handleChange("event_date", e.target.value)} className={`${inputStyles} [color-scheme:dark]`} data-testid="input-event-date" />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.start_time}</Label>
                      <Input type="time" value={form.start_time} onChange={(e) => handleChange("start_time", e.target.value)} className={`${inputStyles} [color-scheme:dark]`} data-testid="input-start-time" />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.end_time}</Label>
                      <Input type="time" value={form.end_time} onChange={(e) => handleChange("end_time", e.target.value)} className={`${inputStyles} [color-scheme:dark]`} data-testid="input-end-time" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.location} *</Label>
                      <Input required value={form.location} onChange={(e) => handleChange("location", e.target.value)} placeholder={t.contact.location_placeholder} className={inputStyles} data-testid="input-location" />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelStyles}>{t.contact.num_people}</Label>
                      <Input value={form.num_people} onChange={(e) => handleChange("num_people", e.target.value)} placeholder="Ex: 200" className={inputStyles} data-testid="input-num-people" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.duration}</Label>
                    <Select value={form.duration} onValueChange={(v) => handleChange("duration", v)}>
                      <SelectTrigger className={inputStyles} data-testid="select-duration"><SelectValue placeholder={t.contact.duration_placeholder} /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-red-900/30 text-white">{t.contact.duration_options.map((d) => (<SelectItem key={d} value={d} className="focus:bg-[#E60000]/20 focus:text-white font-body">{d}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Technical Needs */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="space-y-5"
                  data-testid="form-step-3"
                >
                  <div className="space-y-3">
                    <Label className={labelStyles}>{t.contact.options}</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {t.contact.options_list.map((opt) => (
                        <label key={opt} className="flex items-center gap-3 cursor-pointer group p-3 border border-white/5 hover:border-[#E60000]/30 bg-black/20 transition-all duration-300">
                          <Checkbox
                            checked={form.options.includes(opt)}
                            onCheckedChange={() => toggleOption(opt)}
                            className="border-red-900/50 data-[state=checked]:bg-[#E60000] data-[state=checked]:border-[#E60000] rounded-none"
                            data-testid={`checkbox-${opt.replace(/[\s/()]/g, "-").toLowerCase()}`}
                          />
                          <span className="text-sm text-[#A3A3A3] group-hover:text-white transition-colors font-body">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.budget}</Label>
                    <Select value={form.budget} onValueChange={(v) => handleChange("budget", v)}>
                      <SelectTrigger className={inputStyles} data-testid="select-budget"><SelectValue placeholder={t.contact.budget_placeholder} /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-red-900/30 text-white">{t.contact.budget_options.map((b) => (<SelectItem key={b} value={b} className="focus:bg-[#E60000]/20 focus:text-white font-body">{b}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelStyles}>{t.contact.message}</Label>
                    <Textarea value={form.message} onChange={(e) => handleChange("message", e.target.value)} placeholder={t.contact.message_placeholder} className={`${inputStyles} min-h-[100px]`} data-testid="input-message" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 gap-4" data-testid="form-navigation">
            {step > 1 ? (
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-2 border border-white/15 hover:border-[#E60000]/50 text-white/60 hover:text-white text-sm font-bold uppercase tracking-wider px-6 py-3.5 transition-all duration-300"
                data-testid="form-back-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                {lang === "fr" ? "Retour" : "Back"}
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canGoNext()}
                className="flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-wider px-8 py-3.5 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/40 ml-auto"
                data-testid="form-next-btn"
              >
                {lang === "fr" ? "Suivant" : "Next"}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={status === "loading"}
                className="flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-[0.2em] px-8 py-3.5 transition-all duration-300 hover:shadow-xl hover:shadow-red-900/40 ml-auto"
                data-testid="submit-quote-btn"
              >
                {status === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" />{t.contact.submit}</>}
              </button>
            )}
          </div>
          {status === "error" && <p className="text-[#FF3333] text-sm text-center font-body mt-4" data-testid="form-error">{t.contact.error}</p>}
        </div>
      </div>
    </section>
  );
}
